/**
 * TTS provider backed by the browser `speechSynthesis` API.
 *
 * Design decisions (per spec):
 *   - Serial queue: utterances play one at a time, in order.
 *   - Barge-in: {@link cancelAll} cancels the current utterance and drops the
 *     queue (used when the user starts talking / taps stop).
 *   - NO SSML: we chunk by sentence instead (SSML support is inconsistent and
 *     the Web Speech API does not accept it anyway).
 *   - pt-BR + localService voice preference.
 *   - iOS gesture arming: Safari blocks `speak()` until it has been called once
 *     inside a user gesture. {@link arm} primes the engine with a muted, empty
 *     utterance so subsequent narration plays.
 */
import { type SpeechSynthesisOptions, type SpeechSynthesisProvider, VoiceError } from './types'

const DEFAULT_LANG = 'pt-BR'
/** Split text into speakable sentence chunks; keeps the terminator. */
const SENTENCE_SPLIT_RE = /[^.!?…\n]+[.!?…]*\s*/g

interface QueueItem {
  text: string
  options: SpeechSynthesisOptions
  resolve: () => void
  reject: (err: VoiceError) => void
  /** The utterance currently bound to this item once it starts playing. */
  utterance?: SpeechSynthesisUtterance
}

function getSynthesis(): SpeechSynthesis | null {
  if (typeof window === 'undefined') return null
  return window.speechSynthesis ?? null
}

/** Break a block of text into sentence-sized utterance chunks. */
export function chunkBySentence(text: string): string[] {
  const trimmed = text.trim()
  if (!trimmed) return []
  const matches = trimmed.match(SENTENCE_SPLIT_RE)
  if (!matches) return [trimmed]
  return matches.map((s) => s.trim()).filter((s) => s.length > 0)
}

export class WebSpeechSynthesisProvider implements SpeechSynthesisProvider {
  readonly id = 'web-speech'

  private queue: QueueItem[] = []
  private current: QueueItem | null = null
  private armed = false

  isSupported(): boolean {
    return (
      getSynthesis() !== null &&
      typeof window !== 'undefined' &&
      'SpeechSynthesisUtterance' in window
    )
  }

  isArmed(): boolean {
    return this.armed
  }

  /**
   * Prime the synthesis engine inside a user gesture (iOS requirement).
   *
   * iOS Safari blocks `speak()` until it has been called at least once inside a
   * user gesture. We speak a SILENT (`volume=0`) single-space utterance and let
   * it END NATURALLY — we do NOT `cancel()` it in the same tick, because an
   * immediate cancel can abort the priming before Safari registers the gesture,
   * leaving the user "armed" in our state but still audio-blocked (the classic
   * silent-first-narration bug). Best-effort + idempotent: `armed` is set once
   * the gesture-bound `speak()` call is accepted; if the engine never unlocks,
   * the first real narration's own failure path surfaces it. Safe to call on
   * every tap.
   */
  arm(): void {
    if (this.armed) return
    const synth = getSynthesis()
    if (!synth) return
    try {
      // A single space (not '') so engines that drop empty utterances still get
      // a gesture-bound speak(); volume 0 keeps it inaudible. No cancel() here.
      const primer = new SpeechSynthesisUtterance(' ')
      primer.volume = 0
      synth.speak(primer)
      this.armed = true
    } catch {
      // Arming failed (no engine) — leave unarmed; speak() will report.
    }
  }

  isSpeaking(): boolean {
    return this.current !== null || this.queue.length > 0
  }

  /**
   * Enqueue text for serial playback. The text is chunked by sentence and each
   * chunk is queued as its own utterance; the returned promise resolves once
   * the LAST chunk of this call finishes (or rejects on synthesis failure /
   * barge-in cancel).
   */
  speak(text: string, options: SpeechSynthesisOptions = {}): Promise<void> {
    const synth = getSynthesis()
    if (!synth) {
      return Promise.reject(new VoiceError('not-supported'))
    }

    const chunks = chunkBySentence(text)
    if (chunks.length === 0) {
      return Promise.resolve()
    }

    return new Promise<void>((resolve, reject) => {
      // Only the final chunk settles the caller's promise; earlier chunks
      // share a no-op resolve so the queue still advances.
      chunks.forEach((chunk, index) => {
        const isLast = index === chunks.length - 1
        this.queue.push({
          text: chunk,
          options,
          resolve: isLast ? resolve : () => {},
          reject: isLast ? reject : () => {},
        })
      })
      this.pump()
    })
  }

  /** Barge-in: stop now and clear everything pending. */
  cancelAll(): void {
    const synth = getSynthesis()
    // Snapshot what we're abandoning BEFORE clearing shared state. The cancelled
    // utterance's onend/onerror may fire asynchronously on a later tick; the
    // per-utterance identity guard in pump() makes those callbacks no-ops, so it
    // is safe to null `current` now and resolve the abandoned promises here.
    const abandoned = this.current ? [this.current, ...this.queue] : [...this.queue]
    this.queue = []
    this.current = null
    synth?.cancel()
    // Resolve abandoned promises so awaiting callers don't hang.
    for (const item of abandoned) item.resolve()
  }

  /** Advance the queue if idle. */
  private pump(): void {
    if (this.current || this.queue.length === 0) return
    const synth = getSynthesis()
    if (!synth) return

    const item = this.queue.shift()
    if (!item) return
    this.current = item

    let utterance: SpeechSynthesisUtterance
    try {
      utterance = new SpeechSynthesisUtterance(item.text)
    } catch {
      this.current = null
      item.reject(new VoiceError('synthesis-failed'))
      this.pump()
      return
    }

    utterance.lang = item.options.lang ?? DEFAULT_LANG
    utterance.rate = item.options.rate ?? 1
    utterance.pitch = item.options.pitch ?? 1
    const voice = this.pickVoice(synth, utterance.lang)
    if (voice) utterance.voice = voice

    // Bind this utterance to the active item. The handlers below check identity
    // so a stale onend/onerror from a *cancelled* utterance (which the browser
    // delivers asynchronously, on a later tick, after cancel()) can NEVER wipe
    // the NEW current item or re-pump it. Without this, a speak() that lands
    // between cancelAll() and the cancelled utterance's delayed callback would be
    // corrupted (double-speak / wrong rejection) — the barge-in race.
    item.utterance = utterance

    utterance.onend = () => {
      // Orphan guard: ignore callbacks from an utterance that is no longer the
      // active one (cancelled or already replaced).
      if (this.current !== item) return
      this.current = null
      item.resolve()
      this.pump()
    }

    utterance.onerror = () => {
      if (this.current !== item) return
      this.current = null
      item.reject(new VoiceError('synthesis-failed'))
      this.pump()
    }

    synth.speak(utterance)
  }

  /**
   * Prefer a pt-BR local (on-device) voice for privacy + latency; fall back to
   * any pt-BR voice, then to none (engine default).
   */
  private pickVoice(synth: SpeechSynthesis, lang: string): SpeechSynthesisVoice | null {
    const voices = synth.getVoices()
    if (!voices || voices.length === 0) return null
    const target = lang.toLowerCase()
    const matchesLang = (v: SpeechSynthesisVoice): boolean => v.lang.toLowerCase() === target
    return voices.find((v) => matchesLang(v) && v.localService) ?? voices.find(matchesLang) ?? null
  }
}
